import { LAST_UPDATED } from './legalMeta'

/**
 * Shipping & Delivery Policy.
 *
 * Reflects the shipping stack as built: Shiprocket order creation, AWB
 * assignment, pickup, scan-based tracking with an hourly fallback sync, pincode
 * serviceability and COD availability on the product page
 * (`DeliveryEstimate.jsx` → `api.shipping.serviceability()`), customer tracking
 * (`api.dashboard.trackOrder()`), the NDR re-attempt response
 * (`api.dashboard.ndrResponse()`), and Shiprocket GST invoices once shipped.
 * See `docs/project-context.md` §7 and `docs/tracking-plan.md`.
 */
export const SHIPPING_POLICY = {
  slug: 'shipping-policy',
  badge: 'Legal',
  title: 'Shipping & Delivery Policy',
  intro:
    'Everything we send physically — gemstones, rudraksha, yantras, pooja items and temple prasad — and how dispatch, delivery, tracking and failed attempts work.',
  updated: LAST_UPDATED,
  summary: [
    'We ship across India through our courier partners. Check your pincode on the product page before ordering.',
    'In-stock items are dispatched within 1-3 working days; energized items and prasad take longer because the ritual happens first.',
    'Delivery usually takes 3-7 working days after dispatch, and longer for remote pincodes.',
    'You get a tracking link once the parcel has an AWB number, and you can track it from your order page.',
    'If a delivery fails, you can ask for another attempt from your order page rather than waiting for the parcel to come back.',
  ],
  sections: [
    {
      heading: '1. Where we deliver',
      body: [
        'We deliver across India through third-party courier partners engaged via our logistics aggregator. Coverage depends on your pincode and on the courier serving it.',
        'Every product page has a pincode check. Enter your pincode before you order and it will tell you whether we deliver there, roughly how long it takes, and whether cash on delivery is available for that address. If your pincode is not serviceable, the check says so — please do not place the order, as we will have to cancel and refund it.',
        'We do not currently ship outside India. If you want an item delivered overseas, write to us{contact} before ordering and we will tell you honestly whether it can be arranged.',
      ],
    },
    {
      heading: '2. Dispatch timelines',
      list: [
        'In-stock items — dispatched within 1 to 3 working days of a confirmed order.',
        'Items with energization, abhishek or a pooja performed over them — dispatched after that ritual is completed, which usually adds 2 to 7 working days, depending on the ritual and the muhurat. The product page tells you what to expect.',
        'Certified gemstones that are being lab-tested or certified to your order — dispatched once the certificate is issued.',
        'Temple prasad and raksha sutra included in a pooja package — couriered within 2 to 3 days of the pooja being performed.',
        'Made-to-order, strung or sized items — dispatched on the timeline stated on the listing.',
      ],
      body: [
        'Working days mean Monday to Saturday, excluding public holidays at our dispatch location. Orders confirmed after cut-off, on a Sunday or on a holiday are processed on the next working day.',
      ],
    },
    {
      heading: '3. Delivery timelines',
      list: [
        'Metro and major cities — typically 3 to 5 working days after dispatch.',
        'Other cities and towns — typically 4 to 7 working days after dispatch.',
        'Remote, hill, island and restricted-access pincodes — 7 to 12 working days after dispatch, and sometimes longer.',
        'Temple prasad — typically 5 to 7 working days anywhere in India after it is couriered.',
      ],
      note: 'All timelines are good-faith estimates, not guarantees. Once a parcel is handed to the courier, the final leg is in their hands. Weather, strikes, festivals, local restrictions, election movement curbs and courier backlogs can delay a parcel, and we cannot be held liable for a delay caused by a reason outside our control.',
    },
    {
      heading: '4. Shipping charges',
      list: [
        'The shipping charge, if any, is calculated at checkout from your delivery pincode and the weight and dimensions of your parcel, and is shown to you before you pay. The total at checkout is the total you pay — there is nothing to pay the courier on a prepaid order.',
        'Where we offer free shipping above an order value or on a particular item, the product or cart page says so.',
        'Cash on delivery, where available, may carry a handling fee, shown at checkout before you confirm.',
        'Nobody delivering for us is authorised to collect any extra amount at your door on a prepaid order. If someone asks, refuse and tell us{contact}.',
      ],
    },
    {
      heading: '5. Tracking your order',
      body: [
        'Once your parcel is picked up and assigned an AWB number, we record it against your order. Open your order from your dashboard to see the courier name, the AWB number, the scan history as the parcel moves, and the estimated delivery date where the courier provides one. We also send you dispatch and delivery updates by email and notification.',
        'Scan updates come from the courier through our logistics partner. There can be a gap between a parcel moving and the scan appearing — a tracking page that has not changed for a day does not mean the parcel has stopped. If there has been no movement for more than 3 working days, write to us{contact} with your order number and we will chase it with the courier.',
      ],
    },
    {
      heading: '6. Taking delivery',
      list: [
        'Please give a complete address with a landmark, and a mobile number that will be answered — most failed deliveries are a phone that nobody picked up.',
        'Where the courier asks for an OTP or a signature at the door, it is to confirm the right person received the parcel. Share the OTP only with the delivery agent at your door, and never with anyone who calls you asking for it.',
        'Check the outer packing before you accept the parcel. If it is torn, crushed, wet, re-taped or the seal is broken, refuse it and tell us the same day — that is far easier to resolve than a damage claim after acceptance.',
        'If you accept a parcel that turns out to be damaged inside, photograph the item and the packing and tell us within 48 hours, as our Cancellation & Refund Policy sets out.',
      ],
    },
    {
      heading: '7. When a delivery attempt fails',
      body: [
        'Couriers ordinarily make up to three delivery attempts. When an attempt fails, the reason is recorded against your shipment and shown on your order page.',
        'You do not have to wait for the parcel to come back. From the order page you can ask for another attempt, and give a preferred date, a corrected phone number or a landmark to help the courier find you. We pass that straight to the courier.',
        'If every attempt fails, or the parcel is undeliverable or unclaimed, it returns to us. On a prepaid order we refund the item value and deduct the actual to-and-fro shipping cost. On a cash-on-delivery order nothing was paid, and repeated refusals may mean we stop offering that option to your address.',
      ],
    },
    {
      heading: '8. Lost or damaged in transit',
      body: [
        'A parcel that is confirmed lost in transit, or arrives damaged, is our problem to fix, not yours. Tell us{contact} with your order number, and photographs where it arrived damaged.',
        'We will raise it with the courier and, once the facts are established, either send a replacement or refund you in full, including the shipping you paid. We arrange and pay for any return pickup in these cases. Where a courier investigation is needed, it typically takes 7 to 10 working days, and we will keep you posted rather than leave you waiting.',
      ],
    },
    {
      heading: '9. Address changes and redirection',
      body: [
        'Tell us{contact} as soon as possible if you need to change your delivery address. Before dispatch we can usually update it. After dispatch we can only pass a request to the courier, which may refuse it, and a redirection can add several days.',
        'We deliver only to the address on the order. We cannot hand a parcel to a different person at a different address on a phone request, because that is how parcels get stolen.',
      ],
    },
    {
      heading: '10. Packaging, invoices and product declarations',
      list: [
        'Fragile items — gemstones, idols, glass and metal yantras — are packed protectively, and we use discreet outer packing without describing the contents on the outside.',
        'A tax invoice is issued for every order and is available from your order page. Once a parcel has shipped, the invoice you download may be the GST invoice generated through our logistics partner.',
        'Product listings carry the declarations Indian law requires for packaged goods, including net quantity or weight, the country of origin, and the name and address of the seller or packer. If anything on a listing looks incomplete or wrong, tell us and we will correct it.',
      ],
    },
    {
      heading: '11. Returns and refunds',
      body: [
        'Cancelling before dispatch, returning after delivery, what cannot be returned, who pays for return shipping and how long a refund takes are all set out in our Cancellation & Refund Policy.',
      ],
    },
    {
      heading: '12. Contact us',
      body: [
        'For anything about a dispatch, a delay, a tracking link or a parcel that has not arrived, reach out to us{contact} with your order number. If you are not satisfied with the answer, our Grievance Redressal page sets out how to escalate.',
      ],
    },
  ],
}
